import { createContext, ReactNode, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFetchData } from '@/api/apiHooks';
import {
  PanelsResponsePanel,
  RecruitmentMarketingProvider,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

import { ProviderConnectionModal } from './ProviderConnectionModal';

export interface ProviderSettingsModalState {
  provider: RecruitmentMarketingProvider;
  connectionId: string;
  mode: 'confirm' | 'settings';
}

interface ProviderSettingsModalContextValue {
  openModal: (state: ProviderSettingsModalState) => void;
  closeModal: () => void;
}

const ProviderSettingsModalContext = createContext<
  ProviderSettingsModalContextValue | undefined
>(undefined);

export const ProviderSettingsModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [modalState, setModalState] =
    useState<ProviderSettingsModalState | null>(null);
  const { id: panelId = '', companyId = '' } = useParams<{
    id?: string;
    companyId?: string;
  }>();

  // When the current modal was opened. Only panel data fetched after this is
  // trusted to seed it, see `isSavedConfigLoading` below.
  const [openedAt, setOpenedAt] = useState(0);

  // The saved mapping lives on the panel's embedded connection, and is read
  // here rather than passed in by the caller so it stays in step with the
  // panel data — the card opens the modal before that data has necessarily
  // loaded.
  const {
    data: panelData,
    dataUpdatedAt,
    errorUpdatedAt,
  } = useFetchData<PanelsResponsePanel>({
    url: URLS.getPanel(panelId),
    enabled: !!panelId && !!modalState,
  });
  const savedConfig = panelData?.recruitment_marketing_connections?.find(
    (connection) => connection.provider === modalState?.provider
  )?.provider_config;

  /**
   * The modal seeds its fields once, so it has to wait for panel data that is
   * current as of this open — cached data can predate a mapping saved moments
   * ago (its refetch still in flight) or changed elsewhere. With `staleTime` 0
   * the query always refetches when a modal opens, so this settles once that
   * request does, whether it succeeds or fails.
   */
  const isSavedConfigLoading =
    !!panelId && Math.max(dataUpdatedAt, errorUpdatedAt) < openedAt;

  const openModal = (state: ProviderSettingsModalState) => {
    setOpenedAt(Date.now());
    setModalState(state);
  };

  const closeModal = () => setModalState(null);

  return (
    <ProviderSettingsModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modalState && (
        <ProviderConnectionModal
          isOpen
          provider={modalState.provider}
          companyId={companyId}
          panelId={panelId}
          connectionId={modalState.connectionId}
          savedConfig={savedConfig}
          isSavedConfigLoading={isSavedConfigLoading}
          mode={modalState.mode}
          onClose={closeModal}
          onSaved={closeModal}
        />
      )}
    </ProviderSettingsModalContext.Provider>
  );
};

export const useProviderSettingsModal = () => {
  const context = useContext(ProviderSettingsModalContext);

  if (!context) {
    throw new Error(
      'useProviderSettingsModal must be used within a ProviderSettingsModalProvider'
    );
  }

  return context;
};
